/* eslint-disable max-lines, max-lines-per-function, max-statements */
import { ApolloError, useMutation } from "@apollo/client";
import { useToast } from "@bluframe/blublocks";
import { mutations } from "@bluframe/mightychroma-mightytool-graphql-schema";
import socialsConfig from "config/socials";
import useUser from "hooks/useUser";
import { useCallback, useEffect, useMemo, useState } from "react";
const CONFLICT = 409;
const COOLDOWN_TIMER_BUFFER_MS = 250;
const HEX_RADIX = 16;
const MAX_ACTIVITY_ENTRIES = 25;
const MS_PER_SECOND = 1000;
const RANDOM_SUFFIX_SLICE_INDEX = 2;
const STORAGE_KEY = "socials.jobTriggerActivity.v1";
const { socials: { SOCIALS_DISCOVERY_RUN, SOCIALS_FOLLOWER_SYNC_RUN } } = mutations;
const createEntryId = () => `${Date.now().toString(HEX_RADIX)}-${Math.random()
    .toString(HEX_RADIX)
    .slice(RANDOM_SUFFIX_SLICE_INDEX)}`;
const parseActivity = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return [];
        return parsed
            .filter((entry) => entry && typeof entry === "object")
            .slice(0, MAX_ACTIVITY_ENTRIES);
    }
    catch {
        return [];
    }
};
const persistActivity = (activity) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(activity.slice(0, MAX_ACTIVITY_ENTRIES)));
    }
    catch {
        // Ignore storage failures (private mode, quota, etc.)
    }
};
const getGraphQLErrorCode = (error) => {
    if (!(error instanceof ApolloError))
        return null;
    const code = error.graphQLErrors?.[0]?.extensions?.code;
    if (typeof code === "number")
        return code;
    if (typeof code === "string") {
        const parsed = Number(code);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};
const getGraphQLErrorMessage = (error) => {
    if (error instanceof ApolloError) {
        const graphQLError = error.graphQLErrors?.[0];
        if (graphQLError?.message)
            return graphQLError.message;
        return error.message;
    }
    if (error instanceof Error)
        return error.message;
    return "Unknown error";
};
const getTriggerCooldownUntil = () => new Date(Date.now() + socialsConfig.JOB_TRIGGER_COOLDOWN_SECONDS * MS_PER_SECOND);
const parseValidDate = (value) => {
    if (!value)
        return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const getCooldownTimeoutMs = (cooldownUntil) => {
    const parsed = parseValidDate(cooldownUntil);
    if (!parsed)
        return null;
    const remainingMs = parsed.getTime() - Date.now();
    if (remainingMs <= 0)
        return 0;
    return remainingMs + COOLDOWN_TIMER_BUFFER_MS;
};
const getAccountDisabledReason = (accountId) => accountId ? null : "Missing socials account id";
/**
 * useJobTriggers
 *
 * Manages Socials job trigger UI state for Discovery ingest and Follower sync:
 * - Queues jobs via GraphQL mutations and handles conflict (409) responses.
 * - Tracks running/cooldown/error state so the UI can disable triggers correctly.
 * - Persists a small local activity log in localStorage for operator visibility.
 *
 * @returns {UseJobTriggersResult} Trigger state, activity log, and run callbacks.
 *
 */
const useJobTriggers = () => {
    const user = useUser();
    const { pushToast } = useToast();
    const [activity, setActivity] = useState(parseActivity);
    const [discoveryIngestDynamic, setDiscoveryIngestDynamic] = useState({
        cooldownUntil: null,
        errorMessage: null,
        isRunning: false
    });
    const [followerSyncDynamic, setFollowerSyncDynamic] = useState({
        cooldownUntil: null,
        errorMessage: null,
        isRunning: false
    });
    const accountId = socialsConfig.ACCOUNT_ID;
    const accountDisabledReason = getAccountDisabledReason(accountId);
    const appendActivity = useCallback((entry) => {
        setActivity((prev) => {
            const next = [{ ...entry, id: createEntryId() }, ...prev].slice(0, MAX_ACTIVITY_ENTRIES);
            persistActivity(next);
            return next;
        });
    }, []);
    const [socialsDiscoveryRun] = useMutation(SOCIALS_DISCOVERY_RUN);
    const [socialsFollowerSyncRun] = useMutation(SOCIALS_FOLLOWER_SYNC_RUN);
    const discoveryIngest = useMemo(() => ({
        ...discoveryIngestDynamic,
        disabledReason: accountDisabledReason
    }), [accountDisabledReason, discoveryIngestDynamic]);
    const followerSync = useMemo(() => ({
        ...followerSyncDynamic,
        disabledReason: accountDisabledReason
    }), [accountDisabledReason, followerSyncDynamic]);
    useEffect(() => {
        const timeoutMs = getCooldownTimeoutMs(discoveryIngestDynamic.cooldownUntil);
        if (timeoutMs === null)
            return undefined;
        const timeoutId = setTimeout(() => {
            setDiscoveryIngestDynamic((prev) => ({
                ...prev,
                cooldownUntil: null
            }));
        }, timeoutMs);
        return () => clearTimeout(timeoutId);
    }, [discoveryIngestDynamic.cooldownUntil]);
    useEffect(() => {
        const timeoutMs = getCooldownTimeoutMs(followerSyncDynamic.cooldownUntil);
        if (timeoutMs === null)
            return undefined;
        const timeoutId = setTimeout(() => {
            setFollowerSyncDynamic((prev) => ({
                ...prev,
                cooldownUntil: null
            }));
        }, timeoutMs);
        return () => clearTimeout(timeoutId);
    }, [followerSyncDynamic.cooldownUntil]);
    const userLabel = user.username ?? user.firstName ?? "Unknown user";
    const onRunDiscoveryIngest = useCallback(async (keywordsOverride) => {
        if (accountDisabledReason) {
            pushToast({
                message: "Set REACT_APP_SOCIALS_ACCOUNT_ID to enable job triggers.",
                title: "Missing socials account",
                tone: "alert"
            });
            return;
        }
        setDiscoveryIngestDynamic((prev) => ({
            ...prev,
            errorMessage: null,
            isRunning: true
        }));
        try {
            const sanitizedKeywords = keywordsOverride
                ?.map((keyword) => keyword.trim())
                .filter(Boolean);
            const socialsDiscoveryRunInput = sanitizedKeywords?.length
                ? { accountId, keywordsOverride: sanitizedKeywords }
                : { accountId };
            const result = await socialsDiscoveryRun({
                variables: {
                    socialsDiscoveryRunInput
                }
            });
            const message = result.data?.socialsDiscoveryRun.message ?? null;
            setDiscoveryIngestDynamic((prev) => ({
                ...prev,
                cooldownUntil: getTriggerCooldownUntil(),
                errorMessage: null,
                isRunning: false
            }));
            appendActivity({
                at: new Date().toISOString(),
                job: "discoveryIngest",
                message,
                status: "queued",
                user: userLabel
            });
            pushToast({
                message: message ?? "Discovery ingest queued.",
                title: "Discovery ingest",
                tone: "success"
            });
        }
        catch (error) {
            const code = getGraphQLErrorCode(error);
            const message = getGraphQLErrorMessage(error);
            if (code === CONFLICT) {
                setDiscoveryIngestDynamic((prev) => ({
                    ...prev,
                    cooldownUntil: getTriggerCooldownUntil(),
                    errorMessage: null,
                    isRunning: false
                }));
                appendActivity({
                    at: new Date().toISOString(),
                    job: "discoveryIngest",
                    message,
                    status: "conflict",
                    user: userLabel
                });
                pushToast({
                    message,
                    title: "Discovery ingest already running",
                    tone: "warning"
                });
                return;
            }
            setDiscoveryIngestDynamic((prev) => ({
                ...prev,
                errorMessage: message,
                isRunning: false
            }));
            appendActivity({
                at: new Date().toISOString(),
                job: "discoveryIngest",
                message,
                status: "error",
                user: userLabel
            });
            pushToast({
                message,
                title: "Discovery ingest failed",
                tone: "alert"
            });
        }
    }, [
        accountDisabledReason,
        accountId,
        appendActivity,
        pushToast,
        socialsDiscoveryRun,
        userLabel
    ]);
    const onRunFollowerSync = useCallback(async () => {
        if (accountDisabledReason) {
            pushToast({
                message: "Set REACT_APP_SOCIALS_ACCOUNT_ID to enable job triggers.",
                title: "Missing socials account",
                tone: "alert"
            });
            return;
        }
        setFollowerSyncDynamic((prev) => ({
            ...prev,
            errorMessage: null,
            isRunning: true
        }));
        try {
            const result = await socialsFollowerSyncRun({
                variables: {
                    socialsFollowerSyncRunInput: {
                        accountId
                    }
                }
            });
            const message = result.data?.socialsFollowerSyncRun.message ?? null;
            setFollowerSyncDynamic((prev) => ({
                ...prev,
                cooldownUntil: getTriggerCooldownUntil(),
                errorMessage: null,
                isRunning: false
            }));
            appendActivity({
                at: new Date().toISOString(),
                job: "followerSync",
                message,
                status: "queued",
                user: userLabel
            });
            pushToast({
                message: message ?? "Follower sync queued.",
                title: "Follower sync",
                tone: "success"
            });
        }
        catch (error) {
            const code = getGraphQLErrorCode(error);
            const message = getGraphQLErrorMessage(error);
            if (code === CONFLICT) {
                setFollowerSyncDynamic((prev) => ({
                    ...prev,
                    cooldownUntil: getTriggerCooldownUntil(),
                    errorMessage: null,
                    isRunning: false
                }));
                appendActivity({
                    at: new Date().toISOString(),
                    job: "followerSync",
                    message,
                    status: "conflict",
                    user: userLabel
                });
                pushToast({
                    message,
                    title: "Follower sync already running",
                    tone: "warning"
                });
                return;
            }
            setFollowerSyncDynamic((prev) => ({
                ...prev,
                errorMessage: message,
                isRunning: false
            }));
            appendActivity({
                at: new Date().toISOString(),
                job: "followerSync",
                message,
                status: "error",
                user: userLabel
            });
            pushToast({
                message,
                title: "Follower sync failed",
                tone: "alert"
            });
        }
    }, [
        accountDisabledReason,
        accountId,
        appendActivity,
        pushToast,
        socialsFollowerSyncRun,
        userLabel
    ]);
    return {
        activity,
        discoveryIngest,
        followerSync,
        onRunDiscoveryIngest,
        onRunFollowerSync
    };
};
export default useJobTriggers;
